import type { WaQueryMessagesResult } from '@bt-common/interfaces/whatsapp-tool-contracts.interface';
import type { BtSlimMessage } from '@bt-common/schemas/wa/message';
import { isWireError, WireResponse } from '@common/services/post-message-to-whatsapp-functions';

/**
 * Project the `waQueryMessagesTool` result into the bare message array the AI
 * Suggest-Reply flow consumes.
 *
 * The page tool resolves with EITHER a success envelope
 * (`WaQueryMessagesResult = { messages, paging? }`) OR a `WireErrorEnvelope`
 * (when the page handler throws), and the client wrapper can return `undefined`
 * on timeout. The button used to do `Array.isArray(result)` directly on this —
 * which is ALWAYS false for the envelope, so it threw "Failed to retrieve
 * messages" on every click (#113). Accessing `result.messages` only after
 * narrowing out the error/undefined is the strictly-typed fix: tsc now rejects
 * any future caller that forgets to handle the error branch.
 *
 * Returns the messages array on success, or `null` on any failure shape so the
 * caller can surface a precise error instead of a blanket one.
 */
export function unwrapQueryMessages(
  result: WireResponse<WaQueryMessagesResult> | undefined,
): BtSlimMessage[] | null {
  if (!result || isWireError(result) || !Array.isArray(result.messages)) {
    return null;
  }
  return result.messages;
}
